import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {buildWebSocketUrl} from '../utils/buildWebSocketUrl';
import {captureWebsocketError} from '../utils/captureWebsocketError';
import {getSubscriptionId} from '../utils/getSubscriptionId';
import {detachSocket} from './detachSocket';
import {handleSocketClose} from './handleSocketClose';
import {handleSocketMessage} from './handleSocketMessage';
import {scheduleReconnect} from './scheduleReconnect';
import {stopForMisconfiguration} from './stopForMisconfiguration';
import {KEEP_ALIVE_TIMEOUT_FIVE_MINUTES_IN_MS, WEBSOCKET_PROTOCOL} from './subscriptionConstants';
import type {SocketContext, SubscriptionDeps, SubscriptionState} from './subscriptionTypes';

/**
 * Opens a new WebSocket and wires its lifecycle handlers. Called on start,
 * after every successful refresh, and after every backoff.
 *
 * Detaches the previous socket FIRST so its queued onclose, stale keep-alive
 * timer, or buffered onmessage cannot act on the successor we're about to
 * create. Safe to call even if there was no previous socket.
 */
export const openSocket = (state: SubscriptionState, deps: SubscriptionDeps) => {
  if (state.isCleaningUp) {
    return;
  }

  state.detachCurrentSocket?.();

  // A recoverable failure keeps the self-heal loop running; a configuration
  // fault stops it, because every further attempt would fail the same way.
  const url = match(buildWebSocketUrl())
    .returnType<string | null>()
    .with({status: 'ready'}, (result) => result.url)
    .with({status: 'unauthenticated'}, () => {
      scheduleReconnect(state);
      return null;
    })
    .with({status: 'misconfigured'}, (result) => {
      stopForMisconfiguration(result.reason, state);
      return null;
    })
    .exhaustive();

  if (isNil(url)) {
    return;
  }

  let socket: WebSocket;
  try {
    socket = new WebSocket(url, [WEBSOCKET_PROTOCOL]);
  } catch (error) {
    captureWebsocketError(error);
    scheduleReconnect(state);
    return;
  }

  state.socket = socket;

  const ctx: SocketContext = {
    socket,
    subscriptionId: getSubscriptionId(deps.getState()),
    keepAliveTimeoutMs: KEEP_ALIVE_TIMEOUT_FIVE_MINUTES_IN_MS,
    keepAliveTimeout: undefined,
  };

  state.detachCurrentSocket = () => detachSocket(ctx);

  socket.onopen = () => socket.send(JSON.stringify({type: 'connection_init'}));
  socket.onerror = captureWebsocketError;
  socket.onclose = (event) => handleSocketClose(event, state, ctx);
  socket.onmessage = (event) => handleSocketMessage(event, state, deps, ctx);
};
